import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Info,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
  Brain,
  Calculator,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

/*
  MathRace

  Inspired by the Montreal Imaging Stress Task (MIST):
  - Time-pressured arithmetic tasks
  - Dynamic difficulty or negative/pressuring feedback to keep success rate ~50%
  - Induces mild stress, measuring how quickly & accurately you respond

  Color Theme: from-rose-500 to-pink-600, text-rose-300
*/

const MathRace = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game phases: intro, tutorial, play, results
  const [gameMode, setGameMode] = useState("intro");
  const [gameCompleted, setGameCompleted] = useState(false);

  // Difficulty: easy, standard, advanced
  const [difficulty, setDifficulty] = useState("standard");

  // Score & Leaderboard
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // Feedback Overlay
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Instructions Modal
  const [showInstructions, setShowInstructions] = useState(false);

  // MathRace-Specific State
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(20);

  // Tracking correct/incorrect
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);

  // Timer & gameplay
  const [timeLeft, setTimeLeft] = useState(60);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const gameActive = useRef(false);

  // Timestamps for average response time
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [averageResponseTime, setAverageResponseTime] = useState(0);

  // Start & end game timestamps
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);

  // Current math problem
  const [problem, setProblem] = useState({ question: "", answer: 0 });
  const [answerOptions, setAnswerOptions] = useState([]); // array of possible answers
  const [correctAnswer, setCorrectAnswer] = useState(null);

  // difficulty settings
  // for MIST, we vary how quickly the user must answer & the complexity
  const difficultySettings = {
    easy: {
      timeLimit: 60,
      maxOperand: 10, // smaller numbers
      timePerQuestion: 5 * 1000, // 5 sec
      distractors: 2, // # of incorrect answers
    },
    standard: {
      timeLimit: 60,
      maxOperand: 20,
      timePerQuestion: 4 * 1000,
      distractors: 2,
    },
    advanced: {
      timeLimit: 60,
      maxOperand: 50,
      timePerQuestion: 3 * 1000,
      distractors: 3, // more distractors, e.g. 1 correct + 3 wrong
    },
  };

  // Tutorial
  const [tutorialStep, setTutorialStep] = useState(0);

  // Colors for DSST-based game (processing speed & stress)
  const primaryGradient = "from-rose-500 to-pink-600";
  const textAccent = "text-rose-300";
  const highlightColor = "bg-gradient-to-r from-red-500 to-pink-600";

  // Fetch high scores from Firebase
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // This doc holds the leaderboards for MathRace
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "math-race"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const levelKey = difficulty.toString(); // 'easy' | 'standard' | 'advanced'
          const scores =
            data.leaderboards && data.leaderboards[levelKey]
              ? data.leaderboards[levelKey]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // Timer logic
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Start Game
  const startGame = () => {
    setScore(0);
    setCurrentRound(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setResponseTimes([]);
    setAverageResponseTime(0);

    setTimeLeft(difficultySettings[difficulty].timeLimit);
    setIsPlaying(true);
    gameActive.current = true;
    setGameStartTime(Date.now());
    setGameEndTime(null);

    // Start first round
    startNewRound();
  };

  // End Game
  const endGame = () => {
    setIsPlaying(false);
    gameActive.current = false;
    clearInterval(timerRef.current);
    setGameEndTime(Date.now());
    setGameCompleted(currentRound >= totalRounds || timeLeft <= 1);

    // Calculate final average response time
    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    // Move to results
    setGameMode("results");
  };

  // Generate a new MIST-like arithmetic problem
  const generateProblem = () => {
    const { maxOperand } = difficultySettings[difficulty];

    // Randomly pick an operation or just use addition/subtraction
    const operations = ["+", "-", "*"];
    // Weighted so that multiplication doesn't appear too frequently if you prefer
    const op = operations[Math.floor(Math.random() * operations.length)];

    // random numbers
    let a = Math.floor(Math.random() * maxOperand) + 1;
    let b = Math.floor(Math.random() * maxOperand) + 1;

    // to keep multiplication from being too big
    if (op === "*") {
      // keep the numbers smaller, or do a second random
      a = Math.floor(Math.random() * (maxOperand / 2)) + 1;
      b = Math.floor(Math.random() * (maxOperand / 2)) + 1;
    }

    let correctAns = 0;
    switch (op) {
      case "+":
        correctAns = a + b;
        break;
      case "-":
        correctAns = a - b;
        break;
      case "*":
        correctAns = a * b;
        break;
      default:
        correctAns = a + b;
        break;
    }

    const question = `${a} ${op} ${b}`;
    return { question, answer: correctAns };
  };

  // Start a new round: generate a problem, distractors, etc.
  const startNewRound = () => {
    if (!gameActive.current || currentRound >= totalRounds) {
      endGame();
      return;
    }

    const newProb = generateProblem();
    setProblem(newProb);

    // generate distractor answers
    const distractorCount = difficultySettings[difficulty].distractors;
    const distractors = [];
    // push random answers that aren't correct
    while (distractors.length < distractorCount) {
      const randAns = newProb.answer + (Math.floor(Math.random() * 6) - 3); // random offset
      // prevent duplicates & not equal to correct answer
      if (randAns !== newProb.answer && !distractors.includes(randAns)) {
        distractors.push(randAns);
      }
    }

    const allAnswers = [newProb.answer, ...distractors];
    // shuffle
    allAnswers.sort(() => Math.random() - 0.5);
    setAnswerOptions(allAnswers);
    setCorrectAnswer(newProb.answer);

    setCurrentRound((prev) => prev + 1);

    // set up timing
    setResponseStartTime(Date.now());

    // If MIST style, we can time-limit each question. If user doesn't respond in time, auto-wrong
    const { timePerQuestion } = difficultySettings[difficulty];
    setTimeout(() => {
      // if still the same question, user hasn't answered => count it as incorrect
      // but only if isPlaying is still true
      if (gameActive.current && problem === newProb) {
        handleOptionSelect(null, true);
      }
    }, timePerQuestion);
  };

  // Called when user picks an answer. If "isTimeout" is true, means they didn't pick in time
  const handleOptionSelect = (chosenAns, isTimeout = false) => {
    if (!isPlaying) return;

    const rt = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, rt]);

    let isCorrect = false;
    // If timed out or no selection => automatically wrong
    if (!isTimeout && chosenAns === correctAnswer) {
      isCorrect = true;
    }

    if (isCorrect) {
      setCorrectResponses((prev) => prev + 1);

      // scoring approach
      // base points + speed bonus
      let basePoints = 15; // each correct
      let speedBonus = 1; // minimal
      /* 
        We'll break it down by reaction-time tiers 
        (similar to your other games):
        - < 250 ms => +10
        - < 350 ms => +8
        - < 500 ms => +6
        - < 1000 ms => +4
        - < 1500 ms => +2
        - >= 1500 ms => +1
        => max ~ 25 points per correct => 20 rounds => 500 max 
      */
      if (rt < 250) {
        speedBonus = 10;
      } else if (rt < 350) {
        speedBonus = 8;
      } else if (rt < 500) {
        speedBonus = 6;
      } else if (rt < 1000) {
        speedBonus = 4;
      } else if (rt < 1500) {
        speedBonus = 2;
      } else {
        speedBonus = 1;
      }

      const roundScore = basePoints + speedBonus;
      setScore((prev) => prev + roundScore);

      setFeedbackCorrect(true);
      setFeedbackMessage("Correct!");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 500);
    } else {
      setIncorrectResponses((prev) => prev + 1);
      // small penalty
      setScore((prev) => (prev > 0 ? prev - 5 : 0));

      setFeedbackCorrect(false);
      setFeedbackMessage(isTimeout ? "Too Slow!" : "Incorrect");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 500);
    }

    // proceed
    if (currentRound < totalRounds) {
      const delay = 500;
      setTimeout(() => {
        startNewRound();
      }, delay);
    } else {
      setTimeout(() => {
        endGame();
      }, 500);
    }
  };

  // Save score to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "math-race",
        gameId: "math-race",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        correctResponses,
        incorrectResponses,
        averageResponseTime,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "math-race"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Sorting: score > faster avg time > fewer errors
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.averageResponseTime !== b.averageResponseTime)
          return a.averageResponseTime - b.averageResponseTime;
        return (a.incorrectResponses || 0) - (b.incorrectResponses || 0);
      };

      // 🔁 Transaction to update leaderboard + user
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = difficulty.toString();
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(gameData, currentLeaderboard[4]) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 📜 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset UI
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // Tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      // Done with tutorial
      setGameMode("play");
      startGame();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className={`text-xl md:text-2xl font-bold ${textAccent}`}>
              Math Race
            </h1>
            <div className="p-2 bg-rose-500/10 rounded-full">
              <Calculator className="text-red-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-500/10 rounded-lg">
                      <Calculator className="text-red-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      Math Race (MIST-Inspired)
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6 text-slate-300">
                  <p>
                    <strong>Math Race</strong> is based on the Montreal Imaging
                    Stress Task (MIST). Solve arithmetic problems under time
                    pressure. Difficulty and negative feedback keep you on your
                    toes—great for training mental sharpness under stress.
                  </p>
                  <p>
                    Score big by answering quickly and accurately, but watch
                    out: each mistake costs you points!
                  </p>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:brightness-110 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 ${highlightColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Calculator size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Math Race
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  A fast-paced arithmetic challenge. Solve as many problems as
                  you can under tight deadlines. Earn more points for faster
                  responses—just like the MIST stress task!
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-rose-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">Mental Math</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Sharpen arithmetic under pressure
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-rose-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">Time Pressure</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Short windows to answer, forcing quick thinking
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-rose-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Stress & Rewards
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Experience mild stress, then score big with fast accurate
                    responses
                  </p>
                </div>
              </div>

              {/* Difficulty selection & start buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {["easy", "standard", "advanced"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors capitalize ${
                          difficulty === level
                            ? `bg-gradient-to-r from-rose-500 to-pink-600 text-white`
                            : `bg-slate-700 text-slate-300 hover:bg-slate-600`
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-rose-500 to-pink-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* Leaderboard */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Resp. Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Errors
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((hs, index) => {
                          let formattedName = hs.userName || "Anonymous";
                          if (formattedName.includes(" ")) {
                            const parts = formattedName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={index}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300 capitalize">
                                {hs.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {hs.averageResponseTime
                                  ? `${hs.averageResponseTime} ms`
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {hs.incorrectResponses ?? 0}
                              </td>
                              <td className="py-2 px-4 text-right text-rose-font-medium">
                                {hs.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Math Race
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <strong>Math Race</strong> is inspired by the MIST,
                        which induces mild stress by forcing you to solve math
                        under intense time pressure.
                      </p>
                      <p className="text-slate-300">
                        This helps train you to stay focused under pressure.
                        Quick mental calculations also sharpen your cognitive
                        agility.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        You’ll see a simple arithmetic problem, e.g.{" "}
                        <code className="bg-slate-800 px-1 py-0.5 rounded">
                          7 + 4
                        </code>
                        , then must choose the correct answer from multiple
                        options—fast!
                      </p>
                      <p className="text-slate-300">
                        If you take too long, you auto-lose points. If you rush
                        and guess incorrectly, there’s a penalty. Aim for a
                        balance of speed and accuracy.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        **Scoring**:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>+15 points per correct answer</li>
                          <li>Speed bonus: up to +10 for instant solutions</li>
                          <li>-5 for wrong or late answers</li>
                        </ul>
                      </p>
                      <p className="text-slate-300">
                        **Time**: You have around 60 seconds total, but each
                        question also has its own short countdown. The game ends
                        when time’s up or you finish all {totalRounds} rounds.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Keep calm, focus on each problem, and see if you can top
                        the leaderboard—without panicking under pressure!
                      </p>
                      <p className="text-slate-300">Ready to race?</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-8">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-rose-500 to-pink-600 hover:brightness-110 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Gameplay Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-rose-500/20 px-4 py-2 rounded-lg">
                  <span className="text-rose-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="ml-4 bg-rose-500/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Problem Prompt */}
              <div className="text-center mb-8">
                <h3 className="text-white font-medium mb-2">Solve Quickly!</h3>
                <div className="bg-slate-800 inline-block px-4 py-3 rounded-lg">
                  <span className="text-2xl font-bold text-rose-300">
                    {problem.question}
                  </span>
                </div>
              </div>

              {/* Answer Options */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {answerOptions.map((ans, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleOptionSelect(ans, false)}
                    disabled={!isPlaying || showFeedback}
                    className="py-3 px-6 bg-slate-800 hover:bg-slate-700 
                      text-slate-100 rounded-lg transition-colors text-xl font-semibold"
                  >
                    {ans}
                  </button>
                ))}
              </div>

              {/* Feedback Overlay */}
              <AnimatePresence>
                {showFeedback && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10"
                  >
                    <div className="text-center">
                      <div
                        className={`text-3xl font-bold ${
                          feedbackCorrect ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {feedbackMessage}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Stats */}
              <div className="mt-8 max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Round</div>
                  <div className="text-white font-medium">
                    {currentRound}/{totalRounds}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Correct</div>
                  <div className="text-green-400 font-medium">
                    {correctResponses}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {incorrectResponses}
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">
                    Avg. Time (ms)
                  </div>
                  <div className="text-white font-medium">
                    {responseTimes.length > 0
                      ? Math.round(
                          responseTimes.reduce((a, b) => a + b, 0) /
                            responseTimes.length
                        )
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to end the game early?"
                      )
                    ) {
                      endGame();
                    }
                  }}
                  className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                >
                  End Game
                </button>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 ${highlightColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed Math Race on {difficulty} difficulty.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-red-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-rose-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Errors</span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Response Time</span>
                      <span className="text-white">
                        {averageResponseTime > 0
                          ? `${averageResponseTime} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-red-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-rose-500 to-pink-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <ul className="space-y-2 text-sm">
                      <li className="text-slate-300">
                        <strong>Quick & Accurate:</strong> Higher speed bonus
                        leads to top scores.
                      </li>
                      <li className="text-slate-300">
                        <strong>Errors & Timeouts:</strong> cost you points and
                        lower your accuracy rate.
                      </li>
                      <li className="text-slate-300">
                        Aim to maintain composure under pressure—just like the
                        real MIST lab test!
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bottom Buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-rose-500 to-pink-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MathRace;
